import { PDFDocument, StandardFonts, rgb, degrees } from "pdf-lib";

export interface WatermarkUser {
  name: string;
  email: string;
}

const sanitizeForWinAnsi = (s: string): string => s.replace(/[^\x00-\xff]/g, "?");

const generateDownloadId = async (seed: string): Promise<string> => {
  const buf = new TextEncoder().encode(seed);
  const hash = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(hash))
    .slice(0, 4)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("")
    .toUpperCase();
};

export async function watermarkPdf(
  pdfUrl: string,
  user: WatermarkUser
): Promise<Uint8Array> {
  const response = await fetch(pdfUrl);
  if (!response.ok) {
    throw new Error(`PDF konnte nicht geladen werden (HTTP ${response.status})`);
  }
  const buffer = await response.arrayBuffer();
  return watermarkPdfBuffer(buffer, user);
}

export async function watermarkPdfBuffer(
  buffer: ArrayBuffer | Uint8Array,
  user: WatermarkUser
): Promise<Uint8Array> {
  const doc = await PDFDocument.load(buffer);
  const font = await doc.embedFont(StandardFonts.Helvetica);

  const now = new Date();
  const dateStr = now.toLocaleDateString("de-DE", {
    day: "2-digit", month: "2-digit", year: "numeric",
  });
  const timeStr = now.toLocaleTimeString("de-DE", {
    hour: "2-digit", minute: "2-digit",
  });

  const name = sanitizeForWinAnsi(user.name);
  const email = sanitizeForWinAnsi(user.email);
  const idSeed = `${user.email.toLowerCase().trim()}|${dateStr} ${timeStr}`;
  const downloadId = await generateDownloadId(idSeed);

  const sep = "•";
  const lines: Array<string> = [
    `VERTRAULICH ${sep} ${name}`,
    email,
    `Heruntergeladen am ${dateStr} um ${timeStr} Uhr`,
    `ID ${sep} ${downloadId}`,
  ];
  const footerText = `${email} ${sep} ${dateStr} ${timeStr} ${sep} ID ${downloadId} ${sep} Vertraulich, Weiterleitung untersagt`;

  const lineUnits = lines.map((t) => font.widthOfTextAtSize(t, 1));
  const widerUnit = Math.max(...lineUnits);
  const N = lines.length;

  for (const page of doc.getPages()) {
    const { width, height } = page.getSize();
    const cx = width / 2;
    const cy = height / 2;

    const pageDiag = Math.sqrt(width * width + height * height);
    const targetDiagWidth = pageDiag * 0.7;
    const diagFontSize = Math.max(
      10,
      Math.min(22, targetDiagWidth / widerUnit)
    );
    const lineHeight = diagFontSize * 1.3;

    lines.forEach((text, i) => {
      const offset = (i - (N - 1) / 2) * lineHeight;
      const w = diagFontSize * lineUnits[i];
      const lineCx = cx + offset * Math.SQRT1_2;
      const lineCy = cy - offset * Math.SQRT1_2;
      page.drawText(text, {
        x: lineCx - 0.5 * w * Math.SQRT1_2,
        y: lineCy - 0.5 * w * Math.SQRT1_2,
        size: diagFontSize,
        font,
        color: rgb(0.5, 0.5, 0.5),
        opacity: 0.18,
        rotate: degrees(45),
      });
    });

    const footerSize = 7;
    const footerWidth = font.widthOfTextAtSize(footerText, footerSize);
    page.drawText(footerText, {
      x: Math.max(12, (width - footerWidth) / 2),
      y: 10,
      size: footerSize,
      font,
      color: rgb(0.35, 0.35, 0.35),
      opacity: 0.75,
    });
  }

  return doc.save();
}
